import { useAtomValue } from "@effect/atom-react";
import { useEffect, useMemo, useState } from "react";
import type { EnvironmentId, ModelCatalog, ModelSelection } from "@cleo/contracts";
import { createModelSelection, getModelSelectionOptionDescriptors } from "@cleo/shared/model";
import { serverEnvironment } from "../state/server";
import { deriveProviderInstanceEntries, NO_PROVIDER_MODEL_SELECTION } from "../providerInstances";
import { ProviderModelPicker } from "./chat/ProviderModelPicker";
import { ManageModelsButton } from "./local-models/ManageModelsButton";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "./ui/select";
import { useWorldViewIfSeated } from "../neucleos/worldView";
import { useNeucleosWorldStore } from "../neucleos/worldStore";
import { readModelCatalog } from "../neucleos/modelCatalogClient";
import { managedComposerModels } from "./chat/modelChooserRoot";

/** A birth choice names the new session only. Null keeps its parent's choice. */
export function SessionBirthModelChoice({
  environmentId,
  worldKey,
  value,
  onChange,
  disabled,
  allowInheritance = true,
  settingsLabel,
}: {
  environmentId: EnvironmentId;
  worldKey?: string | null | undefined;
  value: ModelSelection | null;
  onChange: (value: ModelSelection | null) => void;
  disabled: boolean;
  /** Only a session with a parent can inherit a model choice. */
  allowInheritance?: boolean;
  /** Settings uses compact controls and the selected model's reasoning choices. */
  settingsLabel?: string | undefined;
}) {
  const providers = useAtomValue(serverEnvironment.providersValueAtom(environmentId));
  const entries = useMemo(() => deriveProviderInstanceEntries(providers ?? []), [providers]);
  const options = useMemo(
    () => new Map(entries.map((entry) => [entry.instanceId, entry.models])),
    [entries],
  );
  const selected = value ?? NO_PROVIDER_MODEL_SELECTION;
  const worldView = useWorldViewIfSeated();
  const catalogWorldKey =
    worldKey !== undefined
      ? worldKey
      : worldView?.state.kind === "world-ready"
        ? worldView.state.worldKey
        : null;
  const grantRevision = useNeucleosWorldStore((state) => state.grantRevision);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [catalogResult, setCatalogResult] = useState<{
    worldKey: string;
    grantRevision: number;
    catalog: ModelCatalog | null;
    refusal: string | null;
  } | null>(null);
  const needsCatalog = Boolean(settingsLabel) && (selected.instanceId === "neucleos" || pickerOpen);
  useEffect(() => {
    if (!needsCatalog || !catalogWorldKey) return;
    let cancelled = false;
    void readModelCatalog(catalogWorldKey).then(
      (catalog) => {
        if (!cancelled)
          setCatalogResult({ worldKey: catalogWorldKey, grantRevision, catalog, refusal: null });
      },
      (error: unknown) => {
        if (!cancelled)
          setCatalogResult({
            worldKey: catalogWorldKey,
            grantRevision,
            catalog: null,
            refusal: error instanceof Error ? error.message : "Models could not be read.",
          });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [needsCatalog, catalogWorldKey, grantRevision]);
  const catalogState =
    catalogResult?.worldKey === catalogWorldKey && catalogResult.grantRevision === grantRevision
      ? catalogResult
      : { catalog: null, refusal: null };
  const selectedModels =
    settingsLabel && selected.instanceId === "neucleos"
      ? managedComposerModels(catalogState.catalog)
      : entries.find((entry) => entry.instanceId === selected.instanceId)?.models;
  const selectedModel = selectedModels?.find((model) => model.slug === selected.model);
  const reasoning = getModelSelectionOptionDescriptors(value, selectedModel?.capabilities).find(
    (descriptor) =>
      ["reasoningEffort", "reasoningLevel", "reasoning", "effort", "thinking"].includes(
        descriptor.id,
      ),
  );
  const reasoningOptions =
    reasoning?.type === "select"
      ? reasoning.options.filter((option) => !reasoning.promptInjectedValues?.includes(option.id))
      : reasoning?.type === "boolean"
        ? [
            { id: "true", label: "On" },
            { id: "false", label: "Off" },
          ]
        : [];
  const reasoningValue =
    reasoning?.currentValue === undefined ? "" : String(reasoning.currentValue);
  const modelPicker = (
    <ProviderModelPicker
      activeInstanceId={selected.instanceId}
      model={
        value?.model ??
        (allowInheritance ? "Use inherited model" : "Select provider, account and model")
      }
      lockedProvider={null}
      governedContext
      worldKey={worldKey}
      {...(settingsLabel ? { catalogState, onOpenChange: setPickerOpen } : {})}
      instanceEntries={entries}
      modelOptionsByInstance={options}
      disabled={disabled}
      triggerAriaLabel={settingsLabel ?? "Model for this session"}
      triggerVariant="outline"
      triggerClassName={
        settingsLabel
          ? "w-64 max-w-full justify-start text-left sm:max-w-64"
          : "w-full max-w-none sm:max-w-none"
      }
      onInstanceModelChange={(instanceId, model) =>
        onChange(createModelSelection(instanceId, model))
      }
    />
  );
  if (settingsLabel) {
    return (
      <div className="flex flex-wrap items-start gap-3">
        <div className="grid min-w-0 gap-1.5">
          <span className="text-xs text-muted-foreground">Model</span>
          {modelPicker}
        </div>
        <div className="grid gap-1.5">
          <span className="text-xs text-muted-foreground">Reasoning</span>
          <Select
            value={reasoningValue}
            disabled={disabled || !value || reasoningOptions.length === 0}
            onValueChange={(next) => {
              if (!value || !reasoning || !reasoningOptions.some((option) => option.id === next))
                return;
              onChange(
                createModelSelection(value.instanceId, value.model, [
                  ...(value.options ?? []).filter((option) => option.id !== reasoning.id),
                  {
                    id: reasoning.id,
                    value: reasoning.type === "boolean" ? next === "true" : next!,
                  },
                ]),
              );
            }}
          >
            <SelectTrigger size="sm" className="w-40" aria-label={`${settingsLabel} reasoning`}>
              <SelectValue>
                {reasoningOptions.find((option) => option.id === reasoningValue)?.label ??
                  (selected.instanceId === "neucleos" && !catalogState.catalog
                    ? catalogState.refusal
                      ? "Unavailable"
                      : "Loading…"
                    : reasoningOptions.length === 0
                      ? "Not configurable"
                      : "Model default")}
              </SelectValue>
            </SelectTrigger>
            <SelectPopup>
              {reasoningOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
          {selected.instanceId === "neucleos" && catalogState.refusal ? (
            <span role="alert" className="max-w-64 text-xs text-destructive">
              {catalogState.refusal}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>Model for this session</span>
        <ManageModelsButton />
      </div>
      {modelPicker}
      {allowInheritance && value !== null && (
        <button
          type="button"
          className="underline underline-offset-2"
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          Use inherited model
        </button>
      )}
      <span className="block leading-4">
        A local model keeps inference on the selected environment’s device. World records and shared
        results still follow the session’s existing rules.
      </span>
    </div>
  );
}
